// ============================================================
// E1：编辑器 AI 操作计划 → 命令总线命令映射 + AI 标记工具
// ============================================================
import type { Model3D } from '@dtwin/shared';
import type { CommandSpec } from '../core/commandBus';
import useSceneStore from '../stores/sceneStore';
import type { EditorOp } from '../api/aiEditor';

export interface OpCommandBuild {
  commands: CommandSpec[];
  /** 被 AI 修改的既有模型（应用后打 aiGenerated 标记） */
  touchedModelIds: string[];
  /** 人类可读的操作说明（面板展示） */
  descriptions: string[];
  /** B1：需同步落库的 3D 数据绑定（应用后由面板调用 sceneBindingStore.setVisualBinding） */
  bindings: Array<{
    modelId: string;
    modelName: string;
    visualType: string;
    deviceId?: string;
    bindPointKey?: string;
    ruleConfig?: Record<string, unknown>;
  }>;
}

export function opsToCommands(ops: EditorOp[], models: Model3D[]): OpCommandBuild {
  const byId = new Map(models.map((m) => [m.id, m]));
  const commands: CommandSpec[] = [];
  const touched = new Set<string>();
  const descriptions: string[] = [];
  const bindings: OpCommandBuild['bindings'] = [];

  for (const op of ops) {
    if (op.op === 'render.preset') {
      if (op.settings.environment && Object.keys(op.settings.environment).length > 0) {
        commands.push({ type: 'scene.environment.set', payload: { patch: op.settings.environment } });
      }
      if (op.settings.effects && Object.keys(op.settings.effects).length > 0) {
        commands.push({ type: 'scene.effects.set', payload: { patch: op.settings.effects } });
      }
      descriptions.push(`应用渲染预设「${op.presetName}」${op.reason ? `（${op.reason}）` : ''}`);
      continue;
    }
    // A4：换基座（替换全部模型；服务端已展开 manifest → 模型规格）
    if (op.op === 'base.load') {
      const existingIds = models.map((m) => m.id);
      if (existingIds.length > 0) {
        commands.push({ type: 'model.remove', payload: { ids: existingIds } });
      }
      for (const spec of op.models) {
        commands.push({ type: 'model.add', payload: { model: { ...spec, aiGenerated: true } } });
      }
      descriptions.push(`换基座：加载「${op.baseName ?? op.baseId}」（${op.models.length} 个模型，替换原场景）`);
      continue;
    }
    const model = byId.get(op.modelId);
    if (!model) continue; // 模型已不存在 → 跳过
    touched.add(op.modelId);
    switch (op.op) {
      case 'model.visible':
        commands.push({ type: 'model.visible.set', payload: { id: op.modelId, visible: op.visible } });
        descriptions.push(`${op.visible ? '显示' : '隐藏'}「${model.name}」`);
        break;
      case 'model.lock':
        commands.push({ type: 'model.locked.set', payload: { id: op.modelId, locked: op.locked } });
        descriptions.push(`${op.locked ? '锁定' : '解锁'}「${model.name}」`);
        break;
      case 'model.transform': {
        const payload: Record<string, unknown> = { id: op.modelId };
        if (op.position) payload.position = op.position;
        if (op.rotation) payload.rotation = op.rotation;
        if (op.scale) payload.scale = op.scale;
        commands.push({ type: 'model.transform', payload });
        descriptions.push(`调整「${model.name}」变换`);
        break;
      }
      case 'model.remove':
        commands.push({ type: 'model.remove', payload: { ids: [op.modelId] } });
        descriptions.push(`删除「${model.name}」`);
        break;
      case 'model.duplicate':
        commands.push({
          type: 'model.add',
          payload: {
            model: {
              ...model,
              id: undefined,
              name: `${model.name} 副本`,
              position: [model.position[0] + 2, model.position[1], model.position[2]],
              aiGenerated: true,
            },
          },
        });
        descriptions.push(`复制「${model.name}」`);
        break;
      case 'model.materialColor':
        commands.push({
          type: 'model.update',
          payload: {
            id: op.modelId,
            patch: {
              materials: [{ name: 'AI 材质', color: op.color, opacity: 1, metalness: 0.1, roughness: 0.7 }],
            },
          },
        });
        descriptions.push(`「${model.name}」主色替换为 ${op.color}`);
        break;
      case 'model.bind': {
        // B1：设备联动同时写 model.deviceId（命令总线可撤销）；完整绑定记录应用后同步落库
        if (op.deviceId) {
          commands.push({ type: 'model.device.set', payload: { id: op.modelId, deviceId: op.deviceId } });
        }
        bindings.push({
          modelId: op.modelId,
          modelName: model.name,
          visualType: op.visualType,
          ...(op.deviceId ? { deviceId: op.deviceId } : {}),
          ...(op.bindPointKey ? { bindPointKey: op.bindPointKey } : {}),
          ...(op.ruleConfig ? { ruleConfig: op.ruleConfig } : {}),
        });
        descriptions.push(`「${model.name}」数据绑定（${op.visualType}${op.deviceId ? ` ← ${op.deviceId}` : ''}${op.bindPointKey ? `/${op.bindPointKey}` : ''}）`);
        break;
      }
      default:
        break;
    }
  }
  return { commands, touchedModelIds: [...touched], descriptions, bindings };
}

/** 给模型打 AI 标记（直改 store，不产生撤销步；随保存持久化） */
export function markModelsAiGenerated(ids: string[]): number {
  const store = useSceneStore.getState();
  let count = 0;
  for (const id of ids) {
    if (!store.models.some((m) => m.id === id)) continue;
    store.updateModel(id, { aiGenerated: true }, false);
    count++;
  }
  return count;
}

/** 清除全部 AI 标记（返回清除数量） */
export function clearAllAiMarks(): number {
  const store = useSceneStore.getState();
  const ids = store.models.filter((m) => m.aiGenerated).map((m) => m.id);
  for (const id of ids) store.updateModel(id, { aiGenerated: false }, false);
  return ids.length;
}
